<script>
import Layout from "../../../layouts/main";
import Style from "@/views/layouts/style";
import PageHeader from "@/components/Page-header";
import appConfig from "../../../../../app.config";

import UserSSOAPI from "@/services/sso/users/userSSOAPI";
import UserAPI from "@/services/user/userAPI";
import RoleAPI from "@/services/role/roleAPI";
import CompanyAPI from "@/services/company/companyAPI";
import CompanySSOAPI from "@/services/sso/company/companySSO.API";
import { ROLES } from "@/helpers/roles";

import Swal from "sweetalert2";

const ASSIGNED_ROLES = [
  ROLES.CLIENTE_ADMIN,
  ROLES.TALENTO_HUMANO,
  ROLES.RESPONSABLE_OPERATIVO,
];

export default {
  page: {
    title: "Editar rol de usuario",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Layout,
    Style,
    PageHeader,
  },
  data() {
    return {
      title: "Editar rol de usuario",
      items: [
        { text: "", href: "/" },
        { text: "", href: "/" },
        { text: "", active: true },
      ],

      user: {
        id: null,
        name: null,
        email: null,
        sst_id: null,
        role_id: 2,
      },

      exist: false,
      roles: [],
      loading: true,

      // Asignacion de empresas (solo aplica para roles 6/7/8).
      // El listado viene del SSO (catalogo completo, con social_reason),
      // igual que el selector de empresa del topbar. La seleccion se guarda
      // por uuid (id del SSO); el backend crea/enlaza la fila SST por uuid.
      companies: [],
      assignedCompanyUuids: [],
      companySearch: "",
      companyPage: 1,
      companyLastPage: 1,
      loadingCompanies: false,
      savingCompanies: false,
      companySearchTimer: null,
    };
  },
  computed: {
    isAssignedRole() {
      return ASSIGNED_ROLES.includes(Number(this.user.role_id));
    },
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },

    /**
     * Preselecciona (por uuid) las empresas ya asignadas al usuario y carga
     * la primera pagina del catalogo del SSO. El catalogo del SSO contiene
     * TODAS las empresas por su nombre real (social_reason), incluidas las
     * que aun no tienen fila en SST: el usuario asignado las clasificara.
     */
    loadCompanyData() {
      if (this.user.sst_id) {
        CompanyAPI.getCompaniesByUserSst(this.user.sst_id)
          .then((res) => {
            const data = (res && res.data) || [];
            // byUserSst devuelve uuid = id del SSO. Preseleccionamos por uuid.
            this.assignedCompanyUuids = data
              .map((c) => (c.uuid != null ? String(c.uuid) : null))
              .filter(Boolean);
          })
          .catch((error) => {
            console.log(error);
          });
      }

      this.queryCompanies();
    },

    /**
     * Trae una pagina del catalogo de empresas desde el SSO (con busqueda
     * server-side por nombre), igual que el selector de empresa del topbar.
     */
    queryCompanies() {
      this.loadingCompanies = true;
      CompanySSOAPI.getCompaniesBySearch(this.companyPage, this.companySearch)
        .then((res) => {
          this.companies = Array.isArray(res && res.data) ? res.data : [];
          this.companyPage = (res && res.current_page) || this.companyPage;
          this.companyLastPage = (res && res.last_page) || 1;
          this.loadingCompanies = false;
        })
        .catch((error) => {
          console.log(error);
          this.companies = [];
          this.loadingCompanies = false;
        });
    },

    debouncedCompanySearch() {
      if (this.companySearchTimer) clearTimeout(this.companySearchTimer);
      this.companySearchTimer = setTimeout(() => {
        this.companyPage = 1;
        this.queryCompanies();
      }, 350);
    },

    prevCompanyPage() {
      if (this.companyPage <= 1) return;
      this.companyPage -= 1;
      this.queryCompanies();
    },

    nextCompanyPage() {
      if (this.companyPage >= this.companyLastPage) return;
      this.companyPage += 1;
      this.queryCompanies();
    },

    /**
     * Persiste la lista de empresas asignadas al usuario via SST.
     * Solo se ejecuta cuando el rol seleccionado es asignado (6/7/8).
     * Se envian uuids (ids del SSO); el backend crea/enlaza la fila SST.
     */
    async syncCompanies() {
      if (!this.isAssignedRole) return true;
      this.savingCompanies = true;
      try {
        const response = await CompanyAPI.syncCompaniesByNodexum(
          this.user.id,
          this.assignedCompanyUuids
        );
        this.savingCompanies = false;
        return !!(response && response.message === "Empresas asignadas");
      } catch (error) {
        console.log(error);
        this.savingCompanies = false;
        return false;
      }
    },

    save() {
      if (this.exist) {
        UserAPI.update(this.user.sst_id, this.user.id, this.user.role_id)
          .then(async (response) => {
            if (response.message === "User_sst editado con exito") {
              const okCompanies = await this.syncCompanies();
              if (this.isAssignedRole && !okCompanies) {
                Swal.fire({
                  title: "Atencion",
                  text: "Rol actualizado, pero las empresas no se pudieron sincronizar.",
                  type: "warning",
                  icon: "warning",
                  confirmButtonClass: "btn btn-confirm mt-2",
                });
                return;
              }
              Swal.fire({
                title: "¡Éxito!",
                text: "¡Usuario actualizado con exito!",
                type: "success",
                icon: "success",
                confirmButtonClass: "btn btn-confirm mt-2",
              });
              this.goBack();
            } else {
              Swal.fire({
                title: "¡Error!",
                text: "Error en la actualización de usuario",
                type: "warning",
                icon: "warning",
                confirmButtonClass: "btn btn-confirm mt-2",
              });
            }
          })
          .catch(() => {
            Swal.fire({
              title: "¡Error!",
              text: "Error en la actualización de usuario",
              type: "warning",
              icon: "warning",
              confirmButtonClass: "btn btn-confirm mt-2",
            });
          });
      } else {
        const permisos = [
          { permission_id: 1 },
          { permission_id: 3 },
          { permission_id: 4 },
          { permission_id: 5 },
          { permission_id: 6 },
        ];

        UserAPI.save(this.user.id, this.user.role_id, permisos)
          .then(async (response) => {
            if (response.message === "User_sst creado con exito") {
              const okCompanies = await this.syncCompanies();
              if (this.isAssignedRole && !okCompanies) {
                Swal.fire({
                  title: "Atencion",
                  text: "Usuario creado, pero las empresas no se pudieron sincronizar.",
                  type: "warning",
                  icon: "warning",
                  confirmButtonClass: "btn btn-confirm mt-2",
                });
                return;
              }
              Swal.fire({
                title: "¡Éxito!",
                text: "¡Usuario creado con exito!",
                type: "success",
                icon: "success",
                confirmButtonClass: "btn btn-confirm mt-2",
              });
              this.goBack();
            } else {
              Swal.fire({
                title: "¡Error!",
                text: "Error en la creación de usuario",
                type: "warning",
                icon: "warning",
                confirmButtonClass: "btn btn-confirm mt-2",
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    },

    getData() {
      UserAPI.getSSO(this.$route.params.id)
        .then((response) => {
          if (response.length > 0) {
            this.exist = true;
          }

          UserSSOAPI.get(this.$route.params.id)
            .then((resp) => {
              const data = resp.data;

              this.user = {
                id: data.id,
                name: data.name,
                email: data.email,
                sst_id: response.length > 0 ? response[0].id : null,
                role_id: response.length > 0 ? response[0].rol_id : null,
              };

              RoleAPI.getAll()
                .then((resp) => {
                  let roles = resp.Rol.data;

                  roles = roles.map((role) => ({
                    id: role.id,
                    name: role.name.replace("_", " "),
                    status: role.status,
                  }));

                  roles = roles.map((role) => ({
                    id: role.id,
                    name: role.name.replace("ñia", "ñía"),
                    status: role.status,
                  }));

                  roles = roles.map((role) => ({
                    id: role.id,
                    name: role.name.replace("ion", "ión"),
                    status: role.status,
                  }));

                  this.roles = roles.filter(
                    (role) =>
                      role.name !== "Super root" &&
                      role.name !== "Compañía" &&
                      role.name !== "Empleado"
                  );

                  this.roles = this.roles.map((role) => ({
                    id: role.id,
                    name: role.name.replace("Root emisión", "Responsable de diseño"),
                    status: role.status,
                  }));
                  this.loading = false;

                  // Cargar empresas (catalogo + asignadas) en background.
                  this.loadCompanyData();
                })
                .catch((error) => {
                  console.log(error);
                  this.loading = false;
                });
            })
            .catch((err) => {
              console.log(err);
            });
        })
        .catch((error) => {
          console.log(error);
        });
    },
  },
  mounted() {
    this.getData();
  },
  beforeDestroy() {
    if (this.companySearchTimer) clearTimeout(this.companySearchTimer);
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row animated animatedFadeInUp fadeInUp">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-body">
            <div class="row">
              <div class="col-6"></div>
              <div class="col-6 text-right">
                <div @click="goBack" class="btn btn-secondary m-l-5 ml-1" id="tooltip">
                  <i class="fas fa-arrow-left"></i>
                </div>
                <b-tooltip target="tooltip" placement="left">Volver</b-tooltip>
              </div>
            </div>

            <div class="row">
              <div class="col-md-2"></div>
              <div class="col-md-8">
                <div class="card-body">
                  <div v-if="!loading">
                    <form class="form-horizontal form-group" role="form" @submit.prevent="save">
                      <b-form-group label-cols-sm="4" label-cols-lg="3" label="Nombre" label-for="name">
                        <b-form-input disabled v-model="user.name" type="text" id="name"
                          placeholder="Ingrese el nombre"></b-form-input>
                      </b-form-group>

                      <b-form-group label-cols-sm="4" label-cols-lg="3" label="Correo electrónico" label-for="mail">
                        <b-form-input disabled v-model="user.email" type="text" id="mail"
                          placeholder="Ingrese el correo electrónico"></b-form-input>
                      </b-form-group>

                      <b-form-group label-cols-sm="4" label-cols-lg="3" label="Rol" label-for="role">
                        <select class="form-control" v-model="user.role_id">
                          <option v-for="item in roles" :key="item.id" :value="item.id">
                            {{ item.name }}
                          </option>
                        </select>
                      </b-form-group>

                      <!-- Empresas asignadas: solo para roles 6/7/8 -->
                      <div v-if="isAssignedRole" class="mt-4">
                        <hr />
                        <h5 class="mb-3">
                          Empresas asignadas
                          <small class="text-muted">
                            ({{ assignedCompanyUuids.length }} seleccionada{{ assignedCompanyUuids.length === 1 ? '' : 's' }})
                          </small>
                        </h5>
                        <p class="text-muted small">
                          Selecciona las empresas que este usuario podra gestionar. Aparece el catalogo completo de empresas; las que aun no esten clasificadas seran clasificadas por este usuario.
                        </p>

                        <b-form-input
                          v-model="companySearch"
                          placeholder="Buscar empresa por nombre..."
                          class="mb-2"
                          @update="debouncedCompanySearch"
                        />

                        <div v-if="loadingCompanies" class="text-center py-3">
                          <b-spinner variant="primary" small label="Loading..." />
                        </div>
                        <div v-else-if="companies.length === 0" class="text-center py-3 text-muted">
                          No hay empresas que coincidan con la busqueda.
                        </div>
                        <div v-else class="company-list border rounded p-2"
                             style="max-height: 280px; overflow-y: auto;">
                          <b-form-checkbox
                            v-for="c in companies"
                            :key="c.id"
                            v-model="assignedCompanyUuids"
                            :value="String(c.id)"
                            class="company-checkbox"
                          >
                            <span class="text-sm">
                              {{ c.social_reason || ('Empresa ' + c.id) }}
                              <small class="text-muted ml-1">(uuid {{ c.id }})</small>
                            </span>
                          </b-form-checkbox>
                        </div>

                        <!-- Paginacion del catalogo SSO -->
                        <div v-if="!loadingCompanies && companies.length > 0"
                             class="text-center mt-2">
                          <button type="button" class="btn btn-outline-primary btn-sm mx-2"
                                  :disabled="companyPage <= 1" @click="prevCompanyPage">
                            Anterior
                          </button>
                          <span class="small">{{ companyPage }} / {{ companyLastPage }}</span>
                          <button type="button" class="btn btn-outline-primary btn-sm mx-2"
                                  :disabled="companyPage >= companyLastPage" @click="nextCompanyPage">
                            Siguiente
                          </button>
                        </div>
                      </div>

                      <div class="form-group text-right m-b-0 mt-3">
                        <button id="save" class="btn btn-primary" type="submit"
                                :disabled="savingCompanies">
                          {{ savingCompanies ? 'Guardando...' : 'Guardar' }}
                        </button>
                        <button type="button" @click="goBack" class="btn btn-secondary m-l-5 ml-1">
                          Cancelar
                        </button>
                      </div>
                    </form>
                  </div>
                  <div v-else>
                    <div class="text-center">
                      <b-spinner variant="primary" label="Loading..."></b-spinner>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-1"></div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.company-list .company-checkbox {
  padding: 4px 6px;
  border-bottom: 1px solid #f1f1f1;
}
.company-list .company-checkbox:last-child {
  border-bottom: none;
}
</style>
